import React from "react";
import { Box, Typography } from "@mui/material";
import type { CargoOperationDetail } from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { formatInstant } from "../utils/formatInstant";
import { InfoRow, InfoValue } from "./InfoRow";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.detail.operations-info`;

const OperationsInfoCard: React.FC<{
  detail: CargoOperationDetail;
  totalQuantity: React.ReactNode;
}> = ({ detail, totalQuantity }) => {
  const { t } = useTranslation();
  const hasBelt = detail.finalBeltScale !== undefined && detail.finalBeltScale !== null;
  const hasSurvey = detail.finalDraftSurvey !== undefined && detail.finalDraftSurvey !== null;
  const hasTimes = hasBelt || Boolean(detail.firstAts) || Boolean(detail.lastAtc);
  const submittedBy = detail.requestedBy
    ? [detail.requestedBy, detail.requestingAgencyName].filter(Boolean).join(" - ")
    : null;
  const atb = detail.atb
    ? detail.atbBerth
      ? t(`${L}.atb-berth`, { atb: formatInstant(detail.atb), berth: detail.atbBerth })
      : formatInstant(detail.atb)
    : null;
  return (
    <Box
      data-testid="operations-info-card"
      sx={{
        border: "1px solid var(--stroke-secondary-stroke, #E6EDF2)",
        borderRadius: 1,
        p: 2,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        {t(`${L}.title`)}
      </Typography>
      <Box
        sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, columnGap: 6 }}
      >
        <Box>
          {submittedBy && (
            <InfoRow label={t(`${L}.submitted-by`)}>
              <InfoValue testId="header-submitted-by">{submittedBy}</InfoValue>
            </InfoRow>
          )}
          {atb && (
            <InfoRow label={t(`${L}.atb`)}>
              <InfoValue testId="drybulk-cargo-operation-atb">{atb}</InfoValue>
            </InfoRow>
          )}
          {hasTimes && (
            <InfoRow label={t(`${L}.ats`)}>
              <InfoValue testId="header-first-ats">{formatInstant(detail.firstAts)}</InfoValue>
            </InfoRow>
          )}
          <InfoRow label={t(`${L}.total-quantity`)}>{totalQuantity}</InfoRow>
          {hasSurvey && (
            <InfoRow label={t(`${L}.final-draft-survey`)}>
              <InfoValue testId="header-final-draft-survey">
                {String(detail.finalDraftSurvey)}
              </InfoValue>
            </InfoRow>
          )}
        </Box>
        <Box>
          {hasTimes && (
            <InfoRow label={t(`${L}.atc`)}>
              <InfoValue testId="header-last-atc">{formatInstant(detail.lastAtc)}</InfoValue>
            </InfoRow>
          )}
          {hasBelt && (
            <InfoRow label={t(`${L}.actual-quantity`)}>
              <InfoValue testId="header-final-belt-scale">
                {String(detail.finalBeltScale)}
              </InfoValue>
            </InfoRow>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default OperationsInfoCard;
